import { ReceiptText } from 'lucide-react';
import { cn } from '@/lib/utils';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import { ChangeSummaryGroupVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { isZeroMoneyList } from '@/features/dashboard/bookingChanges/utils/money.ts';
import MoneyAmountList from '@/features/dashboard/bookingChanges/components/primitives/MoneyAmountList.tsx';
import { summaryImpactValues } from './changePackageCopy.ts';
import {
  ChangeSummaryTableVariant,
  summaryGutterClass,
} from './changeSummaryTableLayout.ts';

interface ChangeSummaryPersonRowProps {
  group: ChangeSummaryGroupVm;
  variant: ChangeSummaryTableVariant;
}

/**
 * Pasek osoby (albo całej rezerwacji) otwierający jej pozycje. Stoi poza siatką kolumn — nazwisko i
 * suma czytają się lepiej rozpięte na całej szerokości niż wciśnięte w kolumny.
 *
 * Jaśniejszy od swoich wierszy, bo to on dzieli tabelę na bloki; zmiany pod nim
 * są treścią, nie nagłówkiem, i mają się temu podporządkować.
 */
const ChangeSummaryPersonRow = ({
  group,
  variant,
}: ChangeSummaryPersonRowProps) => (
  <div
    role="row"
    className={cn(
      summaryGutterClass(variant),
      'flex flex-wrap items-center gap-x-2.5 gap-y-0.5 border-t border-accent-border bg-accent-100 py-2.5',
    )}
  >
    <InitialsAvatar initials={group.person?.initials} tone="plain" size="sm">
      {group.person ? undefined : <ReceiptText className="size-3.5" />}
    </InitialsAvatar>
    <span role="rowheader" className="text-[15px] font-bold text-ink">
      {group.title}
    </span>
    <span role="cell" className="text-[13px] text-neutral-500">
      {variant === 'client' ? group.clientRoleLabel : group.roleLabel}
    </span>
    <span
      role="cell"
      className={cn(
        'ml-auto text-[13px] font-semibold tabular-nums',
        isZeroMoneyList(group.totalDelta)
          ? 'text-neutral-500'
          : 'text-neutral-700',
      )}
    >
      <MoneyAmountList
        values={summaryImpactValues(group.totalDelta)}
        variant="signed"
      />
    </span>
  </div>
);

export default ChangeSummaryPersonRow;
